// `import * as React` looks unused under the `jsx: 'automatic'` transform
// (used for standalone serving, see bootstrap.tsx) -- it's required for the
// FEDERATED build path (tsconfig.federation.json, classic transform via
// React.createElement/React.Fragment), which this same file also compiles
// under. See federation.config.mjs and shared-federation-config/react's
// own comments for why the federated build can't use the automatic
// transform: react/jsx-runtime's shared chunk can't be built with correct
// named exports (a confirmed native-federation limitation, not something
// fixable from this app's config alone), and disabling
// jsx-runtime/jsx-dev-runtime auto-sharing left it externalized with no
// chunk at all rather than bundled inline. Classic transform sidesteps the
// whole problem: it only needs the (already correctly shared) 'react'
// package itself.
import * as React from 'react';
import { useEffect, useState } from 'react';
import { CLAIM_JOB_BANK, getStoredSession, hasClaim, onSessionChange } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import { CitizenContextProvider } from '@accenturecodefoundry/mfe-pot-citizen-context';
import { useLocale, useTranslations } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import { loadRuntimeConfig } from '../runtime-config';
import { assetBaseUrl } from './asset-base-url';
import { JobMatchesPage } from './pages/JobMatchesPage';
// Registers the SCDS custom elements this app renders -- explicit here too
// (not just via JobMatchesPage's own imports) since this file is
// federation.config.mjs's actual `./Component` expose target.
import '../register-scds';

/**
 * Does its own setup entirely -- no host-provided `REMOTE_PROVIDERS`
 * equivalent for a React remote (see RemoteRouteHost in
 * shared-federation-runtime for why: React has no DI tree for a host to
 * populate, so the exposed component just does on mount what the Angular
 * remotes' remote-providers.ts + app.config.ts collectively did).
 */
export function App() {
  // Defense in depth: this app validates its own claim independently,
  // never assuming "the shell let me navigate here, so I'm authorized" --
  // see CLAUDE.md's "Security: defense in depth" section.
  const [hasAccess, setHasAccess] = useState(() => hasClaim(getStoredSession(), CLAIM_JOB_BANK));
  // Initialised to the dev default so CitizenContextProvider always mounts with a valid
  // string (avoids a one-render window where useCitizenContext() would have no provider).
  // Updated once runtime config resolves.
  const [citizenContextBaseUrl, setCitizenContextBaseUrl] = useState('http://localhost:3006');
  const locale = useLocale();
  const { t } = useTranslations(assetBaseUrl, locale);

  useEffect(() => onSessionChange((session) => setHasAccess(hasClaim(session, CLAIM_JOB_BANK))), []);

  useEffect(() => {
    let cancelled = false;
    loadRuntimeConfig(assetBaseUrl).then((runtimeConfig) => {
      if (!cancelled) {
        setCitizenContextBaseUrl(runtimeConfig.citizenContextBaseUrl);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!hasAccess) {
    return <p role="alert">{t('auth.signInRequired')}</p>;
  }

  return (
    <CitizenContextProvider baseUrl={citizenContextBaseUrl}>
      <JobMatchesPage />
    </CitizenContextProvider>
  );
}
